import "./ConfirmScreen.css"

import { Button, Modal, Result } from "antd"
import { type RcFile } from "antd/es/upload"
import { type Buffer } from "buffer"
import React, { useEffect, useState } from "react"
import { type IWithGoogleReCaptchaProps, useGoogleReCaptcha } from "react-google-recaptcha-v3"

import ChecklistAPI from "../../APIs/ChecklistAPI"
import { base64ToBlob, toLicensePlate, UploadFileToBlob } from "../../helpers/format"
import { type ApplicationState } from "../../store"
import ConfirmScreenOneDevolucao from "./OneDevolucao"
import ConfirmScreenOneEntrega from "./OneEntrega"

let a = false

type OwnProps = {
  saveLocation: string
  afterConfirm?: () => void
  onLoad?: () => void
  onUnmount?: () => void
  PDFPreviewFile?: Buffer
  handleReturn?: () => void
} & ({
  type: "onefleetentrega"
  formData: ApplicationState["onefleetentrega"]
} | {
  type: "onefleetdevolucao"
  formData: ApplicationState["onefleetdevolucao"]
})

const uploadFormImagesAndGetSignedUrl = async (images: Array<RcFile | Blob>, prefix: string, formId: string, getRecaptcha: () => Promise<string | undefined>) => {
  const signedUrls: string[] = []
  for (let i = 0; i < images.length; i++) {
    const signedUri = await ChecklistAPI.uploadImage(images[i] as Blob, `${prefix}_${i}_${formId}`, await getRecaptcha())
    signedUrls.push(signedUri)
  }

  return signedUrls
}

const ConfirmScreen = (props: OwnProps & Partial<IWithGoogleReCaptchaProps>): React.ReactElement => {
  const [confirmed, setConfirmed] = useState<boolean>(false)
  const { executeRecaptcha } = process.env.NODE_ENV === "production" && process.env.REACT_APP_MY_ENV === "production" ? useGoogleReCaptcha() : { executeRecaptcha: async () => process.env.REACT_APP_DEV_TOKEN as string }

  const { handleReturn } = props

  useEffect(() => { if (props.onLoad) props.onLoad() }, [])
  useEffect(() => () => { if (props.onUnmount) props.onUnmount() }, [])

  const handleVerifyRecaptcha = async () => {
    if (!executeRecaptcha) {
      console.info("Recaptcha has not been loaded")

      return
    }

    const token = await executeRecaptcha("homepage")

    return token
  }

  function preventUnload (e: BeforeUnloadEvent) {
    if (a) {
      e.preventDefault()
      e.returnValue = ""
      return e.returnValue
    }
  }

  const handleConfirm = async () => {
    if (props.afterConfirm && !confirmed) {
      switch (props.type) {
        case "onefleetentrega": {
          const {
            carProblemImages,
            cnhImages,
            licensePlateImages,
            clientPicture,
            clientSignatureImage
          } = props.formData.data

          const recaptchaToken = await handleVerifyRecaptcha()
          if (!recaptchaToken) throw new Error("Falha ao gerar Recaptcha")

          const formatedCnhImages: Blob[] = cnhImages ? cnhImages.map(image => image.originFileObj as Blob) : []
          const formatedLicensePlateImages: Blob[] = licensePlateImages ? licensePlateImages.map(image => image.originFileObj as Blob) : []
          const formatedCarProblemImages: Blob[] = carProblemImages ? carProblemImages.map(image => image.originFileObj as Blob) : []

          const UrlCnhImages = await uploadFormImagesAndGetSignedUrl(formatedCnhImages, "cnh", props.formData.data.formId as string, handleVerifyRecaptcha)
          const UrlLicensePlateImages = await uploadFormImagesAndGetSignedUrl(formatedLicensePlateImages, "license_plate", props.formData.data.formId as string, handleVerifyRecaptcha)
          const UrlCarProblemImages = await uploadFormImagesAndGetSignedUrl(formatedCarProblemImages, "car_problem", props.formData.data.formId as string, handleVerifyRecaptcha)
          const UrlClientPicture = await uploadFormImagesAndGetSignedUrl([clientPicture?.originFileObj as Blob], "client_picture", props.formData.data.formId as string, handleVerifyRecaptcha)
          const UrlClientSignatureImage = await uploadFormImagesAndGetSignedUrl([base64ToBlob(clientSignatureImage as string, "image/png")], "client_signature", props.formData.data.formId as string, handleVerifyRecaptcha)

          await ChecklistAPI.sendForm({
            type: "oneentrega",
            formId: props.formData.data.formId as string,
            email: props.formData.data.email as string,
            name: props.formData.data.name as string,
            cpf: props.formData.data.cpf as string,
            clientPhone: `+${props.formData.data.clientPhoneCountry?.code}${props.formData.data.clientPhone}`,
            company: props.formData.data.company as string,
            licensePlate: props.formData.data.licensePlate as string,
            pickupDate: props.formData.data.pickupDate?.format("YYYY-MM-DDTHH:mm:ss") as string,
            carKm: props.formData.data.carKm as number,
            hasCarProblem: props.formData.data.hasCarProblem,
            carProblemDescription: props.formData.data.carProblemDescription,
            surveyGrade: props.formData.data.surveyGrade as number,
            surveyDescription: props.formData.data.surveyDescription as string,
            carProblemImages: UrlCarProblemImages,
            cnhImages: UrlCnhImages,
            licensePlateImages: UrlLicensePlateImages,
            clientPicture: UrlClientPicture[0],
            clientSignatureImage: UrlClientSignatureImage[0],
            recaptchaToken,
            terms: props.formData.data.terms as boolean,
            model: props.formData.data.model as string,
            dealer: props.formData.data.dealer as string,
            consultantEmail: props.formData.data.consultantEmail as string,
            consultantName: props.formData.data.consultantName as string,
            save: true
          })

          props.afterConfirm()

          Modal.success({
            icon: null,
            bodyStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
            content: (
              <Result
                status='success'
                title={`O checklist foi enviado com sucesso! A página será atualizada em ${process.env.REACT_APP_RELOAD_TIME} segundos.`}
              />
            )
          })

          break
        }

        case "onefleetdevolucao": {
          const {
            car,
            client,
            consultant,
            damagedOrMissingItems,
            formId,
            images,
            signatures
          } = props.formData.data

          const {
            licensePlateFileList,
            carBackFileList,
            carFrontFileList,
            carLeftFileList,
            carRightFileList,
            odometerFileList
          } = images

          const {
            clientPicture,
            clientCNH,
            clientSignatureImage,
            consultantSignatureImage
          } = signatures

          const recaptchaToken = await handleVerifyRecaptcha()
          if (!recaptchaToken) throw new Error("Falha ao gerar Recaptcha")

          const formatedLicensePlateImages: Blob[] = UploadFileToBlob(licensePlateFileList)
          const formatedCarBackImages: Blob[] = UploadFileToBlob(carBackFileList)
          const formatedCarFrontImages: Blob[] = UploadFileToBlob(carFrontFileList)
          const formatedCarLeftImages: Blob[] = UploadFileToBlob(carLeftFileList)
          const formatedCarRightImages: Blob[] = UploadFileToBlob(carRightFileList)
          const formatedOdometerImages: Blob[] = UploadFileToBlob(odometerFileList)

          const [
            UrlLicensePlateImages,
            UrlCarBackImages,
            UrlCarFrontImages,
            UrlCarLeftImages,
            UrlCarRightImages,
            UrlOdometerImages,
            UrlClientPicture,
            UrlCNHPicture,
            UrlClientSignatureImage,
            UrlConsultantSignatureImage
          ] = await Promise.all([
            uploadFormImagesAndGetSignedUrl(formatedLicensePlateImages, "license_plate", formId, handleVerifyRecaptcha),
            uploadFormImagesAndGetSignedUrl(formatedCarBackImages, "car_back", formId, handleVerifyRecaptcha),
            uploadFormImagesAndGetSignedUrl(formatedCarFrontImages, "car_front", formId, handleVerifyRecaptcha),
            uploadFormImagesAndGetSignedUrl(formatedCarLeftImages, "car_left", formId, handleVerifyRecaptcha),
            uploadFormImagesAndGetSignedUrl(formatedCarRightImages, "car_right", formId, handleVerifyRecaptcha),
            uploadFormImagesAndGetSignedUrl(formatedOdometerImages, "odometer", formId, handleVerifyRecaptcha),
            uploadFormImagesAndGetSignedUrl([clientPicture?.originFileObj as Blob], "client_picture", formId, handleVerifyRecaptcha),
            uploadFormImagesAndGetSignedUrl([clientCNH?.originFileObj as Blob], "client_cnh", formId, handleVerifyRecaptcha),
            uploadFormImagesAndGetSignedUrl([base64ToBlob(clientSignatureImage, "image/png")], "client_signature", formId, handleVerifyRecaptcha),
            uploadFormImagesAndGetSignedUrl([base64ToBlob(consultantSignatureImage, "image/png")], "consultant_signature", formId, handleVerifyRecaptcha)
          ])

          const accessoryImages: Record<string, string[]> = {}
          if (damagedOrMissingItems?.images) {
            const accessoryEntries = Object.entries(damagedOrMissingItems.images)

            if (accessoryEntries.length) {
              for (const [code, pictures] of accessoryEntries) {
                accessoryImages[code] = await uploadFormImagesAndGetSignedUrl(UploadFileToBlob(pictures), `accessory_${code}`, formId, handleVerifyRecaptcha)
              }
            }
          }

          await ChecklistAPI.sendForm({
            type: "onedevolucao",
            recaptchaToken,
            save: true,
            formId,
            terms: props.formData.data.terms,

            car: {
              ...car,
              licensePlate: toLicensePlate(car.licensePlate),
              returnDate: car.returnDate.format("YYYY-MM-DDTHH:mm:ss")
            },

            client,
            consultant,
            damagedOrMissingItems: {
              carProblemDescription: damagedOrMissingItems.carProblemDescription,
              accessoriesStatus: damagedOrMissingItems?.accessoriesStatus,
              images: accessoryImages
            },
            images: {
              licensePlateFileList: UrlLicensePlateImages,
              carBackFileList: UrlCarBackImages,
              carFrontFileList: UrlCarFrontImages,
              carLeftFileList: UrlCarLeftImages,
              carRightFileList: UrlCarRightImages,
              odometerFileList: UrlOdometerImages
            },
            signatures: {
              clientPicture: UrlClientPicture[0],
              cnhPicture: UrlCNHPicture[0],
              clientSignatureImage: UrlClientSignatureImage[0],
              consultantSignatureImage: UrlConsultantSignatureImage[0]
            }
          })

          a = false
          window.removeEventListener("beforeunload", preventUnload)
          props.afterConfirm()

          Modal.success({
            icon: null,
            bodyStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
            content: (
              <Result
                status='success'
                title={`O Checklist de Devolução foi enviado com sucesso! A página será atualizada em ${process.env.REACT_APP_RELOAD_TIME} segundos.`}
              />
            )
          })
          break
        }
      }
    }

    setConfirmed(true)

    setTimeout(() => {
      window.location.reload()
    }, Number(process.env.REACT_APP_RELOAD_TIME ?? 0) * 1000)
  }

  window.addEventListener("beforeunload", preventUnload)

  const handleButton = () => {
    if (!confirmed) {
      a = true
      Modal.confirm({
        bodyStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
        content: (<span>Ao clicar em “Confirmar” o checklist será enviado. Tem certeza que deseja prosseguir?</span>),
        okText: "Confirmar",
        cancelText: "Revisar",
        onOk: handleConfirm
      })
    }
  }

  return (
    <>
      <div id="finalForm">
        {
          props.type === "onefleetentrega" && (<ConfirmScreenOneEntrega formData={props.formData} />)
        }
        {
          props.type === "onefleetdevolucao" && (<ConfirmScreenOneDevolucao formData={props.formData}/>)
        }
      </div>
      {
        !confirmed && <Button onClick={handleReturn}>Voltar</Button>
      }
      {
        !confirmed && <Button type="primary" onClick={handleButton}>Confirmar</Button>
      }
    </>
  )
}

export default ConfirmScreen
